/**
 * Générateur — Résoudre des problèmes avec des additions et soustractions de
 * fractions.
 * Attendu ID : 925 | Niveau : 5ème | Domaine : Nombres et calculs / Nombres rationnels
 *
 * Plan de cartes (mise en conformité avec la spécification 925, campagne 5e,
 * 2026-09-22) :
 *
 *   Carte « Parts d'un tout (même dénominateur) » (id parts)
 *     parts_texte_trou_saisie : niveau 1, deux parts de même dénominateur
 *       réunies, puis la part restante (le tout écrit en n-ièmes).
 *     parts_qcm_unique : niveau 1, la part réunie ou la part restante, avec
 *       les distracteurs nommés ci-dessous.
 *
 *   Carte « Composer les parts » (id composer)
 *     composer_texte_trou_saisie : niveau 2, dénominateurs multiples l'un de
 *       l'autre (réécriture, somme, part restante).
 *
 *   Carte « Retour à la quantité » (id quantite)
 *     quantite_texte_trou_saisie : niveau 3, dénominateurs premiers entre eux
 *       ou à facteur commun sans être multiples ; réécriture, somme, reste.
 *     quantite_texte : niveau 3, forme 2 (un effectif), l'unité en suffixe.
 *     quantite_qcm_unique : niveau 3, forme 2, distracteurs nommés.
 *
 *   Carte « Un pourcentage parmi les parts » (id pourcentage)
 *     pourcentage_texte_trou_saisie : niveau 4, trois parts dont une donnée en
 *       pourcentage automatisé, part restante.
 *
 *   Carte « Deux chemins, deux parts » (id chemins)
 *     chemins_qcm_unique : niveau 5, trois étapes (une part « de plus que »,
 *       une part restante) puis comparaison des deux parts obtenues.
 *
 *   Carte « Dépasser l'unité ? » (id depasser)
 *     depasser_qcm_unique + depasser_justification_qcm_unique : variante
 *       explicite où plusieurs touts sont réunis (exclusion 4 de la spec).
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\925.json) :
 *   Bornes appliquées : dénominateurs des fractions de l'énoncé 2..12 ;
 *   numérateurs 1..11 et strictement inférieurs à leur dénominateur ; une ou
 *   deux opérations sur les fractions par problème, une à trois étapes ;
 *   effectif total 12..600, toujours multiple du dénominateur commun ;
 *   pourcentage pris dans la liste fermée (10 % et 50 % retenus, voir « non
 *   tranché ») ; formes de réponse 1 (fraction), 2 (effectif) ; part restante
 *   présente dans six cartes sur neuf.
 *   Exclusions appliquées : aucune multiplication ni division de deux
 *   fractions ; aucun problème sans addition ou soustraction de fractions ;
 *   effectif total divisible par le dénominateur commun ; somme des parts
 *   nommées inférieure au tout, sauf la carte « depasser » (variante
 *   explicite) ; aucun contexte d'angle ni de secteur circulaire ; unité
 *   rappelée par l'énoncé, jamais saisie.
 *   Erreurs visées → mots-clés :
 *     numérateurs et dénominateurs additionnés → numerateurs_et_denominateurs_additionnes
 *     tout non écrit sous forme de fraction    → tout_vaut_1_oublie
 *     fraction du reste au lieu du tout        → fraction_du_reste
 *     « de plus que » lu comme la part elle-même → de_plus_que_confondu
 *     division par le numérateur               → division_par_le_numerateur
 *     fraction donnée là où un effectif est demandé → fraction_au_lieu_d_effectif
 *     unité oubliée dans la réponse en effectif → oubli_unite (NON SERVI, voir
 *       « non tranché » : le type_reponse de la spec interdit de saisir l'unité)
 *     pourcentage non reconnu comme une fraction → pourcentage_non_reconnu
 *   Graduation → cartes : niveau 1 → parts_texte_trou_saisie, parts_qcm_unique ;
 *   niveau 2 → composer_texte_trou_saisie ; niveau 3 → quantite_texte_trou_saisie,
 *   quantite_texte, quantite_qcm_unique ; niveau 4 → pourcentage_texte_trou_saisie ;
 *   niveau 5 → chemins_qcm_unique ; réglage transverse (habillage, plusieurs
 *   touts) → depasser_qcm_unique, depasser_justification_qcm_unique.
 *   Non tranché : 1 % est dans la liste fermée des pourcentages automatisés mais
 *   vaut 1/100, dénominateur hors des bornes (max 12) — seuls 10 % et 50 % sont
 *   tirés. La variable « dénominateur » dit 2..12 et justifie en citant les
 *   centièmes : bornes appliquées telles qu'écrites, aux seules fractions de
 *   l'énoncé (le dénominateur commun et les dénominateurs des distracteurs, qui
 *   sont l'erreur elle-même, n'y sont pas soumis — la spec ne borne pas le
 *   dénominateur commun). L'erreur visée « oublier l'unité » contredit le
 *   type_reponse (« l'unité est rappelée par l'énoncé plutôt que saisie ») :
 *   le type_reponse est appliqué, l'erreur n'est servie par aucune carte.
 *   La forme de réponse 3 (fraction ET effectif dans la même question) n'a pas
 *   de fabrique à deux champs notés pour un point entier : les deux formes sont
 *   servies par deux cartes distinctes de la même rubrique.
 *
 * Arithmétique entière exacte (produits en croix, zéro flottant).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';
import { gcd } from '../../6eme/fractions/fraction';

const ATTENDU_ID = 925;

const L_PARTS = 'Parts d\'un tout (même dénominateur)';
const L_COMPOSER = 'Composer les parts';
const L_QUANTITE = 'Retour à la quantité';
const L_POURCENTAGE = 'Un pourcentage parmi les parts';
const L_CHEMINS = 'Deux chemins, deux parts';
const L_DEPASSER = 'Dépasser l\'unité ?';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Bornes de la spécification ─────────────────────────────────────────
const DEN_MAX = 12;          // variables.denominateur.max
const EFFECTIF_MIN = 12;     // variables.effectif_total.min
const EFFECTIF_MAX = 600;    // variables.effectif_total.max
const POURCENTAGES = [10, 50]; // variables.pourcentage_automatise (1 % exclu, voir en-tête)

const lcm = (a, b) => (a * b) / gcd(a, b);

const katexBrut = (num, den) => (den === 1 ? String(num) : `$\\dfrac{${num}}{${den}}$`);
const memeValeur = (n1, d1, n2, d2) => n1 * d2 === n2 * d1;

// Phrases à trous en lignes KaTeX globales (audit fractions en texte,
// 2026-09-06) : une fraction bâtie autour d'un trou en texte brut
// (« {{3}}/9 ») s'affichait avec sa barre ; chaque ligne est une formule
// entière, texte via \text{}, trou au numérateur avec son étai (motif 620).
const fracTrou = (num, den) => `\\dfrac{{{${num}}}\\rule[-0.55em]{0pt}{0.55em}}{${den}}`;
const T = (s) => `\\text{${s}}`;
// Fraction en style texte pour les phrases-choix d'un QCM (étiquettes longues).
const fx = (num, den) => `$\\frac{${num}}{${den}}$`;

const PRENOMS = ['Steven', 'Alice', 'Yanis', 'Nora', 'Émile', 'Sofia', 'Tom', 'Leïla'];

// ═════════ Carte 1 — Parts d'un tout (niveau 1) ═════════

// « deObjet » porte l'article contracté tout fait : « du gâteau » et non
// « de » + « le gâteau » (accord des groupes nominaux construits, leçon gen_999).
const CONTEXTES_PARTS = [
    { deObjet: 'de la tarte', de: 'de la tarte de leur grand-mère', verbe: 'mange' },
    { deObjet: 'de la pizza', de: 'de la pizza', verbe: 'mange' },
    { deObjet: 'du gâteau', de: 'du gâteau d\'anniversaire', verbe: 'mange' },
    { deObjet: 'de la tablette', de: 'de la tablette de chocolat', verbe: 'mange' },
];

const tirerParts = () => {
    for (let essai = 0; essai < 40; essai++) {
        const n = randomChoice([6, 8, 10, DEN_MAX]);
        const x = randInt(1, n - 3);
        const y = randInt(1, n - x - 2);
        if (x === y) continue; // parts distinctes (évite les distracteurs dégénérés)
        const [p1, p2] = shuffle(PRENOMS).slice(0, 2);
        const ctx = randomChoice(CONTEXTES_PARTS);
        return { n, x, y, p1, p2, ctx };
    }
    return { n: 8, x: 1, y: 2, p1: 'Steven', p2: 'Alice', ctx: CONTEXTES_PARTS[0] };
};

const introParts = ({ n, x, y, p1, p2, ctx }) =>
    `${p1} ${ctx.verbe} $\\dfrac{${x}}{${n}}$ ${ctx.de} et ${p2} en ${ctx.verbe} $\\dfrac{${y}}{${n}}$.`;

const genPartsTrous = () => {
    const t = tirerParts();
    const { n, x, y } = t;
    // Une question par ligne (convention 2026-09-02 : jamais « ; » entre deux
    // trous indépendants, l'impression coupait au milieu).
    const phrase = `$${T('À eux deux, ils ont mangé ')} ${fracTrou(x + y, n)} ${T(` ${t.ctx.deObjet}.`)}$\n`
        + `$${T('Le tout vaut ')} ${fracTrou(n, n)}${T(', il reste donc ')} ${fracTrou(n - x - y, n)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${introParts(t)}</p><p><b>Complète les numérateurs</b> (sans simplifier les fractions).</p>`,
        { ...opts(L_PARTS), mode: 'saisie' });
    payload._meta.params = { n, x, y, niveau: 1, erreurVisee: ['tout_vaut_1_oublie'] };
    return payload;
};

const genPartsQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const t = tirerParts();
        const { n, x, y } = t;
        const sousQuestion = randomChoice(['ensemble', 'reste']);
        const r = sousQuestion === 'ensemble' ? x + y : n - x - y;
        const question = sousQuestion === 'ensemble'
            ? `Quelle fraction ${t.ctx.deObjet} ont-ils mangée à eux deux ?`
            : `Quelle fraction ${t.ctx.deObjet} reste-t-il ?`;
        const candidats = sousQuestion === 'ensemble'
            ? [
                { num: x + y, den: 2 * n, code: 'numerateurs_et_denominateurs_additionnes' },
                { num: n - x - y, den: n, code: 'confusion_mange_reste' },
                { num: y > x ? y - x : x - y, den: n, code: 'soustraction_au_lieu_d_addition' },
            ]
            : [
                { num: x + y, den: n, code: 'confusion_mange_reste' },
                { num: n - x, den: n, code: 'oubli_d_une_part' },
                { num: x + y, den: 2 * n, code: 'numerateurs_et_denominateurs_additionnes' },
            ];
        const paires = [{ num: r, den: n, code: 'correct' }, ...candidats];
        const valides = paires.every(p => p.num > 0)
            && paires.every((p, i) => paires.every((q, j) => i === j || !memeValeur(p.num, p.den, q.num, q.den)));
        if (!valides) continue;
        const correct = katexBrut(r, n);
        const choix = paires.map(p => katexBrut(p.num, p.den));
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, correct,
            `<p>${introParts(t)}</p><p><b>${question}</b> (Fraction non simplifiée.)</p>`,
            opts(L_PARTS));
        payload._meta.params = {
            n, x, y, sousQuestion, niveau: 1,
            distracteurs: candidats.map(p => ({ code: p.code, valeur: katexBrut(p.num, p.den) })),
        };
        return payload;
    }
    // Repli déterministe (1/8 + 2/8, question « ensemble »)
    const correct = katexBrut(3, 8);
    const payload = makeQcm([correct, katexBrut(3, 16), katexBrut(5, 8), katexBrut(1, 8)], correct,
        '<p>Steven mange $\\dfrac{1}{8}$ de la tarte de leur grand-mère et Alice en mange $\\dfrac{2}{8}$.</p>'
        + '<p><b>Quelle fraction de la tarte ont-ils mangée à eux deux ?</b> (Fraction non simplifiée.)</p>',
        opts(L_PARTS));
    payload._meta.params = {
        n: 8, x: 1, y: 2, sousQuestion: 'ensemble', niveau: 1,
        distracteurs: [
            { code: 'numerateurs_et_denominateurs_additionnes', valeur: katexBrut(3, 16) },
            { code: 'confusion_mange_reste', valeur: katexBrut(5, 8) },
            { code: 'soustraction_au_lieu_d_addition', valeur: katexBrut(1, 8) },
        ],
    };
    return payload;
};

// ═════════ Carte 2 — Composer les parts (niveau 2) ═════════

const CONTEXTES_COMPOSER = [
    {
        A: 'au caramel', B: 'aux fruits', C: 'au chocolat',
        intro: 'Un confiseur remplit des sachets de bonbons',
        pA: 'Bonbons au caramel', pAB: 'au caramel et aux fruits ensemble', pC: 'au chocolat (le reste)',
    },
    {
        A: 'de romans', B: 'de bandes dessinées', C: 'de documentaires',
        intro: 'Une bibliothécaire range une étagère',
        pA: 'Romans', pAB: 'romans et bandes dessinées ensemble', pC: 'documentaires (le reste)',
    },
    {
        A: 'de pommiers', B: 'de cerisiers', C: 'de poiriers',
        intro: 'Un jardinier plante un verger',
        pA: 'Pommiers', pAB: 'pommiers et cerisiers ensemble', pC: 'poiriers (le reste)',
    },
];

const majuscule = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const genComposerTrous = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = randomChoice([3, 4, 5]);
        const k = randomChoice([2, 3]);
        const D = d * k;
        if (D > DEN_MAX) continue;          // variables.denominateur.max = 12
        const a = randInt(1, d - 1);
        if (gcd(a, d) !== 1) continue;
        const b = randInt(1, D - a * k - 1);
        if (gcd(b, D) !== 1) continue;      // la part énoncée s'affiche irréductible
        const s = a * k + b;
        const reste = D - s;
        if (reste < 1) continue;
        const ctx = randomChoice(CONTEXTES_COMPOSER);
        // Une part par ligne (convention 2026-09-02 : jamais « ; » entre deux
        // trous indépendants), chaque ligne commençant par une majuscule.
        const phrase = `$${T(`${ctx.pA} : `)} \\dfrac{${a}}{${d}} = ${fracTrou(a * k, D)}$\n`
            + `$${T(`${majuscule(ctx.pAB)} : `)} ${fracTrou(s, D)}$\n`
            + `$${T(`${majuscule(ctx.pC)} : `)} ${fracTrou(reste, D)}$`;
        const payload = makeTexteTrou(phrase,
            `<p>${ctx.intro} : $\\dfrac{${a}}{${d}}$ ${ctx.A}, $\\dfrac{${b}}{${D}}$ ${ctx.B}, le reste ${ctx.C}.</p>`
            + '<p><b>Complète le raisonnement</b> (numérateurs seulement).</p>',
            { ...opts(L_COMPOSER), mode: 'saisie' });
        payload._meta.params = {
            d, k, D, a, b, s, reste, niveau: 2, erreurVisee: ['tout_vaut_1_oublie'],
        };
        return payload;
    }
    // Repli déterministe (1/3 et 2/9 sur 9 parts)
    const payload = makeTexteTrou(
        `$${T('Bonbons au caramel : ')} \\dfrac{1}{3} = ${fracTrou(3, 9)}$\n`
        + `$${T('Au caramel et aux fruits ensemble : ')} ${fracTrou(5, 9)}$\n`
        + `$${T('Au chocolat, le reste : ')} ${fracTrou(4, 9)}$`,
        '<p>Un confiseur remplit des sachets de bonbons : $\\dfrac{1}{3}$ au caramel, $\\dfrac{2}{9}$ aux fruits, le reste au chocolat.</p>'
        + '<p><b>Complète le raisonnement</b> (numérateurs seulement).</p>',
        { ...opts(L_COMPOSER), mode: 'saisie' });
    payload._meta.params = {
        d: 3, k: 3, D: 9, a: 1, b: 2, s: 5, reste: 4, niveau: 2, erreurVisee: ['tout_vaut_1_oublie'],
    };
    return payload;
};

// ═════════ Carte 3 — Retour à la quantité (niveau 3) ═════════

// Couples de dénominateurs 2..12 premiers entre eux (relation 2) ou à facteur
// commun sans être multiples (relation 3) — variables.relation_entre_les_denominateurs.
const COUPLES_QUANTITE = [
    [2, 3], [2, 5], [3, 4], [3, 5], [4, 5], [2, 7], [4, 6], [4, 10], [6, 9],
];

/**
 * ⚠️ CHAQUE fraction doit nommer LE TOUT dont elle est la part, sans exception.
 *
 * « Un groupe en parcourt 2/7, puis 1/2 le deuxième jour » a deux lectures : la moitié
 * du sentier, ou la moitié de ce qui reste. Elles donnent 21 km et 35 km, et le
 * générateur propose justement 35 comme piège, sous le mot-clé `fraction_du_reste`.
 * L'élève qui lisait « la moitié du reste » avait donc raison et était compté faux
 * (retour prof 2026-09-25).
 *
 * Le contexte du budget était déjà correct, il répétait « de son budget ». Les deux
 * autres répètent maintenant leur référence de la même façon. Le champ `reference`
 * nomme ce tout, pour qu'un test puisse vérifier qu'il figure bien devant chaque part.
 */
const CONTEXTES_QUANTITE = [
    {
        reference: 'budget',
        intro: (R, fA, fB) => `Yanis dispose d'un budget mensuel de ${R} €. Il dépense ${fA} de son budget pour le logement et ${fB} de son budget pour les courses.`,
        question: 'Quelle somme lui reste-t-il ?',
        unite: '€', rMin: 200, rMax: EFFECTIF_MAX,
    },
    {
        reference: 'roman',
        intro: (R, fA, fB) => `Un roman compte ${R} pages. Sofia lit ${fA} du roman le matin dans le bus, puis ${fB} du roman le soir.`,
        question: 'Combien de pages lui reste-t-il à lire ?',
        unite: 'pages', rMin: 120, rMax: EFFECTIF_MAX,
    },
    {
        reference: 'sentier',
        intro: (R, fA, fB) => `Un sentier de randonnée mesure ${R} km. Le premier jour, un groupe parcourt ${fA} du sentier, et le deuxième jour ${fB} du sentier.`,
        question: 'Quelle distance reste-t-il à parcourir ?',
        unite: 'km', rMin: 24, rMax: 240,
    },
];

/**
 * Deux parts n1/d1 et n2/d2 (dénominateurs 2..12, relation 2 ou 3), un effectif
 * total R multiple du dénominateur commun D et compris dans 12..600.
 * m = R / D est choisi multiple de gcd(d1, d2) pour que les distracteurs de la
 * carte QCM tombent eux aussi sur des entiers.
 */
const tirerQuantite = () => {
    for (let essai = 0; essai < 120; essai++) {
        const [d1, d2] = shuffle(randomChoice(COUPLES_QUANTITE));
        const D = lcm(d1, d2);
        const g = gcd(d1, d2);
        const n1 = randInt(1, d1 - 1);
        const n2 = randInt(1, d2 - 1);
        if (gcd(n1, d1) !== 1 || gcd(n2, d2) !== 1) continue;
        const u1 = n1 * (D / d1);
        const u2 = n2 * (D / d2);
        const r = D - u1 - u2;
        if (r < 2) continue;                    // numérateur du reste ≠ 1 (vigilance 1)
        const ctx = randomChoice(CONTEXTES_QUANTITE);
        const mMin = Math.ceil(Math.max(ctx.rMin, EFFECTIF_MIN) / D);
        const mMax = Math.floor(Math.min(ctx.rMax, EFFECTIF_MAX) / D);
        if (mMin > mMax) continue;
        let m = randInt(mMin, mMax);
        m = Math.ceil(m / g) * g;               // divisibilité des distracteurs
        if (m < mMin || m > mMax) continue;
        const R = D * m;
        if (R < EFFECTIF_MIN || R > EFFECTIF_MAX) continue;
        return { d1, d2, D, g, n1, n2, u1, u2, r, m, R, ctx };
    }
    // Repli déterministe : 2/5 et 1/4 de 400 €
    return {
        d1: 5, d2: 4, D: 20, g: 1, n1: 2, n2: 1, u1: 8, u2: 5, r: 7, m: 20, R: 400,
        ctx: CONTEXTES_QUANTITE[0],
    };
};

const genQuantiteTrous = () => {
    const { d1, d2, D, n1, n2, u1, u2, r, R, ctx } = tirerQuantite();
    const phrase = `$\\dfrac{${n1}}{${d1}} = ${fracTrou(u1, D)} \\quad ${T('et')} \\quad \\dfrac{${n2}}{${d2}} = ${fracTrou(u2, D)}$\n`
        + `$${T('Fraction utilisée en tout : ')} ${fracTrou(u1 + u2, D)}$\n`
        + `$${T('Le tout vaut ')} ${fracTrou(D, D)}${T(', la fraction restante est ')} ${fracTrou(r, D)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${ctx.intro(R, katexBrut(n1, d1), katexBrut(n2, d2))}</p>`
        + '<p><b>Complète le raisonnement</b> (numérateurs seulement).</p>',
        { ...opts(L_QUANTITE), mode: 'saisie' });
    payload._meta.params = {
        d1, d2, D, n1, n2, u1, u2, r, R, reference: ctx.reference,
        niveau: 3, erreurVisee: ['tout_vaut_1_oublie'],
    };
    return payload;
};

const genQuantiteSaisie = () => {
    const { d1, d2, D, n1, n2, r, m, R, ctx } = tirerQuantite();
    const montant = m * r; // R × r/D, ENTIER par construction (R = D·m)
    const payload = makeSaisie(String(montant),
        `<p>${ctx.intro(R, katexBrut(n1, d1), katexBrut(n2, d2))}</p>`
        + `<p><b>${ctx.question}</b> (Nombre entier, sans unité.)</p>`,
        { ...opts(L_QUANTITE), suffixe: ctx.unite });
    payload._meta.params = {
        d1, d2, D, n1, n2, r, R, montant, niveau: 3,
        erreurVisee: ['division_par_le_numerateur'],
    };
    return payload;
};

const genQuantiteQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const { d1, d2, D, g, n1, n2, r, m, R, ctx } = tirerQuantite();
        const montant = m * r;
        // Fraction du reste : n2/d2 pris sur ce qui reste après n1/d1
        const fractionDuReste = (m / g) * (d1 - n1) * (d2 - n2);
        // Une seule part retranchée
        const oubliDUnePart = m * (D / d1) * (d1 - n1);
        if (!Number.isInteger(fractionDuReste) || !Number.isInteger(oubliDUnePart)) continue;
        if (fractionDuReste < 1 || oubliDUnePart < 1) continue;
        const candidats = [
            { texte: tex(String(fractionDuReste)), code: 'fraction_du_reste' },
            { texte: katexBrut(r, D), code: 'fraction_au_lieu_d_effectif' },
            { texte: tex(String(oubliDUnePart)), code: 'oubli_d_une_part' },
        ];
        const correct = tex(String(montant));
        const choix = [correct, ...candidats.map(c => c.texte)];
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, correct,
            `<p>${ctx.intro(R, katexBrut(n1, d1), katexBrut(n2, d2))}</p>`
            + `<p><b>${ctx.question}</b> (Réponse en ${ctx.unite}.)</p>`,
            opts(L_QUANTITE));
        payload._meta.params = {
            d1, d2, D, n1, n2, r, R, montant, reference: ctx.reference, niveau: 3,
            distracteurs: candidats.map(c => ({ code: c.code, valeur: c.texte })),
        };
        return payload;
    }
    // Repli déterministe : 2/5 et 1/4 de 400 €
    const ctx = CONTEXTES_QUANTITE[0];
    const correct = tex('140');
    const candidats = [
        { texte: tex('180'), code: 'fraction_du_reste' },
        { texte: katexBrut(7, 20), code: 'fraction_au_lieu_d_effectif' },
        { texte: tex('240'), code: 'oubli_d_une_part' },
    ];
    const payload = makeQcm([correct, ...candidats.map(c => c.texte)], correct,
        `<p>${ctx.intro(400, katexBrut(2, 5), katexBrut(1, 4))}</p>`
        + `<p><b>${ctx.question}</b> (Réponse en ${ctx.unite}.)</p>`,
        opts(L_QUANTITE));
    payload._meta.params = {
        d1: 5, d2: 4, D: 20, n1: 2, n2: 1, r: 7, R: 400, montant: 140, niveau: 3,
        distracteurs: candidats.map(c => ({ code: c.code, valeur: c.texte })),
    };
    return payload;
};

// ═════════ Carte 4 — Un pourcentage parmi les parts (niveau 4) ═════════

const CONTEXTES_POURCENTAGE = [
    {
        intro: (fA, fB, pct) => `Sur l'ensemble des livres vendus cette semaine, une librairie a vendu ${fA} de romans, ${fB} de bandes dessinées et ${pct} de magazines. Le reste est constitué de livres scolaires.`,
        reste: 'livres scolaires',
    },
    {
        intro: (fA, fB, pct) => `Sur l'ensemble des élèves d'un collège, ${fA} viennent à vélo, ${fB} viennent à pied et ${pct} viennent en bus. Les autres élèves viennent en voiture.`,
        reste: 'élèves venant en voiture',
    },
];

/**
 * Trois parts sur un dénominateur commun D : n1/d1, n2/d2 (dénominateurs
 * affichés ≤ 12, stricts diviseurs de D pour que la réécriture ait lieu) et le
 * pourcentage, qui vaut pv/D. 10 % = 1/10 et 50 % = 1/2 : leurs dénominateurs
 * naturels tiennent dans les bornes, ce que 1 % (1/100) ne fait pas.
 */
const DIVISEURS_AFFICHABLES = [2, 3, 4, 5, 6, 8, 9, 10, DEN_MAX];

const tirerPourcentage = () => {
    for (let essai = 0; essai < 200; essai++) {
        const pct = randomChoice(POURCENTAGES);
        const D = randomChoice(pct === 10 ? [10, 20] : [6, 8, 10, DEN_MAX, 20]);
        const pv = (D * pct) / 100;
        if (!Number.isInteger(pv) || pv < 1) continue;
        const divs = DIVISEURS_AFFICHABLES.filter(x => D % x === 0 && x < D);
        if (divs.length < 2) continue;
        const d1 = randomChoice(divs);
        const d2 = randomChoice(divs.filter(x => x !== d1));
        const n1 = randInt(1, d1 - 1);
        const n2 = randInt(1, d2 - 1);
        if (gcd(n1, d1) !== 1 || gcd(n2, d2) !== 1) continue;
        // Une part écrite en fraction ne vaut jamais le pourcentage annoncé :
        // « 1/10 à vélo et 10 % en bus » se lit comme une répétition.
        if (n1 * 100 === pct * d1 || n2 * 100 === pct * d2) continue;
        const u1 = n1 * (D / d1);
        const u2 = n2 * (D / d2);
        const utilise = u1 + u2 + pv;
        const reste = D - utilise;
        if (reste < 1) continue;
        return {
            pct, D, pv, d1, n1, u1, d2, n2, u2, utilise, reste,
            pDen: pct === 10 ? 10 : 2,
            ctx: randomChoice(CONTEXTES_POURCENTAGE),
        };
    }
    return {
        pct: 10, D: 10, pv: 1, d1: 5, n1: 1, u1: 2, d2: 2, n2: 1, u2: 5,
        utilise: 8, reste: 2, pDen: 10, ctx: CONTEXTES_POURCENTAGE[0],
    };
};

const genPourcentageTrous = () => {
    const t = tirerPourcentage();
    const { pct, D, pv, d1, n1, u1, d2, n2, u2, utilise, reste, ctx } = t;
    // « % » est un commentaire en TeX : il s'écrit \% dans \text{}.
    const phrase = `$\\dfrac{${n1}}{${d1}} = ${fracTrou(u1, D)}$\n`
        + `$\\dfrac{${n2}}{${d2}} = ${fracTrou(u2, D)}$\n`
        + `$${T(`${pct} \\% du total, c'est `)} ${fracTrou(pv, D)}$\n`
        + `$${T('Parts utilisées en tout : ')} ${fracTrou(utilise, D)}$\n`
        + `$${T('Le tout vaut ')} ${fracTrou(D, D)}${T(`, la part des ${ctx.reste} est `)} ${fracTrou(reste, D)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${ctx.intro(katexBrut(n1, d1), katexBrut(n2, d2), `${pct} %`)}</p>`
        + '<p><b>Complète le raisonnement</b> (numérateurs seulement).</p>',
        { ...opts(L_POURCENTAGE), mode: 'saisie' });
    payload._meta.params = {
        pct, D, pv, d1, n1, u1, d2, n2, u2, utilise, reste, pDen: t.pDen, niveau: 4,
        erreurVisee: ['pourcentage_non_reconnu', 'tout_vaut_1_oublie'],
    };
    return payload;
};

// ═════════ Carte 5 — Deux chemins, deux parts (niveau 5) ═════════

const D_CHEMINS = 12;

const CONTEXTES_CHEMINS = [
    {
        intro: (fJ, fP) => `Dans un club d'arts martiaux, ${fJ} des membres font du judo. Il y a ${fP} des membres de plus au karaté qu'au judo. Les autres membres font de l'aïkido.`,
        B: 'Le karaté', C: 'L\'aïkido', question: 'Quelle activité rassemble le plus de membres, le karaté ou l\'aïkido ?',
    },
    {
        intro: (fJ, fP) => `Dans une école de musique, ${fJ} des élèves jouent du violon. Il y a ${fP} des élèves de plus à la guitare qu'au violon. Les autres élèves jouent de la flûte.`,
        B: 'La guitare', C: 'La flûte', question: 'Quel instrument rassemble le plus d\'élèves, la guitare ou la flûte ?',
    },
];

const DIVISEURS_12 = [2, 3, 4, 6, 12];

/**
 * j/12 le judo, p/12 l'écart, K = j + p le karaté, R = 12 − j − K l'aïkido.
 * On vise tantôt K > R, tantôt R > K, pour que les deux réponses tournent.
 */
const tirerChemins = () => {
    const viserB = randomChoice([true, false]);
    for (let essai = 0; essai < 120; essai++) {
        const dj = randomChoice(DIVISEURS_12);
        const dp = randomChoice(DIVISEURS_12.filter(x => x !== dj));
        const aj = randInt(1, dj - 1);
        const bp = randInt(1, dp - 1);
        if (gcd(aj, dj) !== 1 || gcd(bp, dp) !== 1) continue;
        const j = aj * (D_CHEMINS / dj);
        const p = bp * (D_CHEMINS / dp);
        const K = j + p;
        const R = D_CHEMINS - j - K;
        if (R < 1 || K === R || j + p === 6) continue;
        if ((K > R) !== viserB) continue;
        return { dj, aj, dp, bp, j, p, K, R, gagnant: K > R ? 'B' : 'C' };
    }
    return viserB
        ? { dj: 4, aj: 1, dp: 6, bp: 1, j: 3, p: 2, K: 5, R: 4, gagnant: 'B' }
        : { dj: 6, aj: 1, dp: 12, bp: 1, j: 2, p: 1, K: 3, R: 7, gagnant: 'C' };
};

const genCheminsQcm = () => {
    const t = tirerChemins();
    const ctx = randomChoice(CONTEXTES_CHEMINS);
    const D = D_CHEMINS;
    const bonB = `${ctx.B}, avec ${fx(t.K, D)} du total.`;
    const bonC = `${ctx.C}, avec ${fx(t.R, D)} du total.`;
    const candidats = [
        { texte: `${ctx.B}, avec ${fx(t.p, D)} du total.`, code: 'de_plus_que_confondu' },
        { texte: `${ctx.C}, avec ${fx(D - t.j - t.p, D)} du total.`, code: 'de_plus_que_confondu' },
    ];
    const correct = t.gagnant === 'B' ? bonB : bonC;
    const choix = [bonB, bonC, ...candidats.map(c => c.texte)];
    const payload = makeQcm(choix, correct,
        `<p>${ctx.intro(katexBrut(t.aj, t.dj), katexBrut(t.bp, t.dp))}</p><p><b>${ctx.question}</b></p>`,
        opts(L_CHEMINS));
    payload._meta.params = {
        ...t, D, niveau: 5, distracteurs: candidats.map(c => ({ code: c.code, valeur: c.texte })),
    };
    return payload;
};

// ═════════ Carte 6 — Dépasser l'unité ? (variante « plusieurs touts ») ═════════

const CONTEXTES_DEPASSER = [
    {
        intro: (f1, f2, f3) => `Trois frères veulent acheter un jeu vidéo. Le premier possède ${f1} du prix du jeu, le deuxième ${f2} et le troisième ${f3}.`,
        question: 'Ont-ils assez d\'argent pour acheter ensemble ce jeu ?',
        oui: 'Oui : la somme de leurs parts dépasse le prix du jeu.',
        non: 'Non : la somme de leurs parts est inférieure au prix du jeu.',
        piege1: 'On ne peut pas savoir sans connaître le prix du jeu en euros.',
        piege2: 'Oui : ils sont trois, donc ils ont forcément assez d\'argent.',
    },
    {
        intro: (f1, f2, f3) => `Trois amies participent à une cagnotte pour un cadeau. La première apporte ${f1} du prix du cadeau, la deuxième ${f2} et la troisième ${f3}.`,
        question: 'La cagnotte suffit-elle pour acheter le cadeau ?',
        oui: 'Oui : la somme de leurs parts dépasse le prix du cadeau.',
        non: 'Non : la somme de leurs parts est inférieure au prix du cadeau.',
        piege1: 'On ne peut pas savoir sans connaître le prix du cadeau en euros.',
        piege2: 'Oui : elles sont trois, donc la cagnotte suffit forcément.',
    },
];

/**
 * Trois fractions ramenées à D ≤ 12 : d1 | D et d2 | D (dénominateurs
 * différents), la troisième directement sur D. Somme S ≠ D STRICT, tirée
 * tantôt > D (assez), tantôt < D (pas assez).
 */
const tirerDepasser = () => {
    for (let essai = 0; essai < 120; essai++) {
        const D = randomChoice([10, DEN_MAX]);
        const divs = [2, 3, 4, 5, 6].filter(x => D % x === 0);
        if (divs.length < 2) continue;
        const d1 = randomChoice(divs);
        const d2 = randomChoice(divs.filter(x => x !== d1));
        const m1 = randInt(1, d1 - 1);
        const m2 = randInt(1, d2 - 1);
        if (gcd(m1, d1) !== 1 || gcd(m2, d2) !== 1) continue;
        const n1 = m1 * (D / d1);
        const n2 = m2 * (D / d2);
        const viserOui = randomChoice([true, false]);
        const n3Min = viserOui ? Math.max(1, D + 1 - n1 - n2) : 1;
        const n3Max = viserOui ? Math.round(D * 1.4) - n1 - n2 : D - 1 - n1 - n2;
        if (n3Min > n3Max) continue;
        const n3 = randInt(n3Min, n3Max);
        const S = n1 + n2 + n3;
        if (S === D || n3 < 1 || n3 >= D) continue;
        if (gcd(n3, D) !== 1) continue;     // la troisième part s'affiche irréductible
        return { D, d1, m1, d2, m2, n1, n2, n3, S, assez: S > D };
    }
    return { D: 12, d1: 4, m1: 3, d2: 3, m2: 1, n1: 9, n2: 4, n3: 1, S: 14, assez: true };
};

const genDepasserQcm = () => {
    const t = tirerDepasser();
    const ctx = randomChoice(CONTEXTES_DEPASSER);
    const texte = `<p>${ctx.intro(katexBrut(t.m1, t.d1), katexBrut(t.n3, t.D), katexBrut(t.m2, t.d2))}</p>`
        + `<p><b>${ctx.question}</b></p>`;
    const correct = t.assez ? ctx.oui : ctx.non;
    const choix = [ctx.oui, ctx.non, ctx.piege1, ctx.piege2];
    const payload = makeQcm(choix, correct, texte, opts(L_DEPASSER));
    payload._meta.params = {
        ...t, niveau: 'transverse',
        distracteurs: [
            { code: 'fraction_pas_un_nombre', valeur: ctx.piege1 },
            { code: 'tout_vaut_1_oublie', valeur: ctx.piege2 },
        ],
    };
    return payload;
};

const genDepasserJustifQcm = () => {
    const t = tirerDepasser();
    const ctx = randomChoice(CONTEXTES_DEPASSER);
    const { D, d1, m1, d2, m2, n1, n2, n3, S } = t;
    const texte = `<p>${ctx.intro(katexBrut(m1, d1), katexBrut(n3, D), katexBrut(m2, d2))}</p>`
        + '<p><b>Quel calcul permet de répondre ?</b></p>';
    const comp = (s) => (s > D ? '>' : '<');
    // Fractions en KaTeX de style texte dans ces phrases-choix (audit
    // fractions en texte, 2026-09-06).
    const correct = `${fx(n1, D)} + ${fx(n3, D)} + ${fx(n2, D)} = ${fx(S, D)}, et ${fx(S, D)} ${comp(S)} 1.`;
    const sBrute = m1 + n3 + m2;
    const candidats = [
        {
            texte: `${fx(m1, D)} + ${fx(n3, D)} + ${fx(m2, D)} = ${fx(sBrute, D)}, et ${fx(sBrute, D)} ${comp(sBrute)} 1.`,
            code: 'numerateur_non_converti',
        },
        {
            texte: `${fx(n1, D)} + ${fx(n3, D)} + ${fx(n2, D)} = ${fx(S, D)} : il faut comparer ${fx(S, D)} à 3.`,
            code: 'tout_vaut_1_oublie',
        },
        {
            texte: `${fx(m1, d1)} + ${fx(n3, D)} + ${fx(m2, d2)} = ${fx(m1 + n3 + m2, d1 + D + d2)}.`,
            code: 'numerateurs_et_denominateurs_additionnes',
        },
    ];
    const payload = makeQcm([correct, ...candidats.map(c => c.texte)], correct, texte, opts(L_DEPASSER));
    payload._meta.params = {
        ...t, niveau: 'transverse',
        distracteurs: candidats.map(c => ({ code: c.code, valeur: c.texte })),
    };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    parts_texte_trou_saisie: genPartsTrous,
    parts_qcm_unique: genPartsQcm,
    composer_texte_trou_saisie: genComposerTrous,
    quantite_texte_trou_saisie: genQuantiteTrous,
    quantite_texte: genQuantiteSaisie,
    quantite_qcm_unique: genQuantiteQcm,
    pourcentage_texte_trou_saisie: genPourcentageTrous,
    chemins_qcm_unique: genCheminsQcm,
    depasser_qcm_unique: genDepasserQcm,
    depasser_justification_qcm_unique: genDepasserJustifQcm,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM', texte: 'Saisie',
    justification_qcm_unique: 'QCM justification',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'parts', label: L_PARTS, formatsList: buildFormats('parts') },
    { id: 'composer', label: L_COMPOSER, formatsList: buildFormats('composer') },
    { id: 'quantite', label: L_QUANTITE, formatsList: buildFormats('quantite') },
    { id: 'pourcentage', label: L_POURCENTAGE, formatsList: buildFormats('pourcentage') },
    { id: 'chemins', label: L_CHEMINS, formatsList: buildFormats('chemins') },
    { id: 'depasser', label: L_DEPASSER, formatsList: buildFormats('depasser') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
